// src/utils/fiscalConfig.js
// Configuration fiscale par année - À mettre à jour selon les lois de finances

/**
 * Configuration fiscale par année
 * Sources: BOFiP, Lois de finances
 *
 * IMPORTANT: Ces valeurs doivent être mises à jour chaque année
 * après publication de la loi de finances
 */
export const FISCAL_CONFIG = {
  // 2026: Projections basées sur 2025 (+2% inflation estimée)
  2026: {
    salaryDeduction: {
      rate: 0.10,
      min: 520,
      max: 14900,
    },
    pensionDeduction: {
      rate: 0.10,
      min: 465,
      max: 4540,
    },
    microBIC: {
      vente: 0.71,
      service: 0.50,
    },
    microBNC: 0.34,
    microFoncier: 0.30,
    lmnpMicro: 0.50,
    irBrackets: [
      { min: 0,      max: 11750,  rate: 0.00 },
      { min: 11750,  max: 29960,  rate: 0.11 },
      { min: 29960,  max: 85668,  rate: 0.30 },
      { min: 85668,  max: 184261, rate: 0.41 },
      { min: 184261, max: Infinity, rate: 0.45 },
    ],
    familyQuotient: {
      maxReductionPerHalfPart: 1830,
      singleParentBonus: 4317,
    },
    decote: {
      singleThreshold: 2007,
      coupleThreshold: 3320,
      singleCoef: 908,
      coupleCoef: 1502,
    },
    socialContributions: {
      csg: 0.092,
      crds: 0.005,
      prelevementSolidarite: 0.072,
      total: 0.172,
    },
    corporateTax: {
      reducedRate: 0.15,
      reducedRateLimit: 42500,
      normalRate: 0.25,
    },
    microThresholds: {
      bicVente: 188700,
      bicService: 77700,
      bnc: 77700,
      lmnp: 77700,
      foncierMicro: 15000,
    },
  },

  2025: {
    // === ABATTEMENTS SUR REVENUS === (estimations, à confirmer avec LF 2025)
    salaryDeduction: {
      rate: 0.10,
      min: 510,             // Estimation +3%
      max: 14600,           // Estimation +3%
    },
    pensionDeduction: {
      rate: 0.10,
      min: 455,
      max: 4450,
    },

    // === ABATTEMENTS MICRO-ENTREPRISES ===
    microBIC: {
      vente: 0.71,
      service: 0.50,
    },
    microBNC: 0.34,
    microFoncier: 0.30,
    lmnpMicro: 0.50,

    // === BARÈME IR (Revenus 2025, déclarés en 2026) === Estimation +2%
    irBrackets: [
      { min: 0,      max: 11520,  rate: 0.00 },
      { min: 11520,  max: 29373,  rate: 0.11 },
      { min: 29373,  max: 83988,  rate: 0.30 },
      { min: 83988,  max: 180648, rate: 0.41 },
      { min: 180648, max: Infinity, rate: 0.45 },
    ],

    // === QUOTIENT FAMILIAL ===
    familyQuotient: {
      maxReductionPerHalfPart: 1794,
      singleParentBonus: 4232,
    },

    // === DÉCOTE ===
    decote: {
      singleThreshold: 1968,
      coupleThreshold: 3255,
      singleCoef: 890,
      coupleCoef: 1473,
    },

    // === CONTRIBUTIONS SOCIALES ===
    socialContributions: {
      csg: 0.092,
      crds: 0.005,
      prelevementSolidarite: 0.072,
      total: 0.172,
    },

    // === IMPÔT SUR LES SOCIÉTÉS ===
    corporateTax: {
      reducedRate: 0.15,
      reducedRateLimit: 42500,
      normalRate: 0.25,
    },

    // === SEUILS MICRO-ENTREPRISE ===
    microThresholds: {
      bicVente: 188700,
      bicService: 77700,
      bnc: 77700,
      lmnp: 77700,
      foncierMicro: 15000,
    },
  },

  2024: {
    // === ABATTEMENTS SUR REVENUS ===
    salaryDeduction: {
      rate: 0.10,           // Taux abattement forfaitaire salaires
      min: 495,             // Plancher abattement
      max: 14171,           // Plafond abattement salaires/chômage
    },
    pensionDeduction: {
      rate: 0.10,           // Taux abattement forfaitaire retraites
      min: 442,             // Plancher abattement
      max: 4321,            // Plafond abattement retraites
    },

    // === ABATTEMENTS MICRO-ENTREPRISES ===
    microBIC: {
      vente: 0.71,          // Abattement micro-BIC vente de marchandises
      service: 0.50,        // Abattement micro-BIC prestations de services
    },
    microBNC: 0.34,         // Abattement micro-BNC
    microFoncier: 0.30,     // Abattement micro-foncier
    lmnpMicro: 0.50,        // Abattement LMNP micro-BIC

    // === BARÈME IR (Revenus 2024, déclarés en 2025) ===
    irBrackets: [
      { min: 0,      max: 11294,  rate: 0.00 },
      { min: 11294,  max: 28797,  rate: 0.11 },
      { min: 28797,  max: 82341,  rate: 0.30 },
      { min: 82341,  max: 177106, rate: 0.41 },
      { min: 177106, max: Infinity, rate: 0.45 },
    ],

    // === QUOTIENT FAMILIAL ===
    familyQuotient: {
      maxReductionPerHalfPart: 1759,  // Plafond avantage par demi-part
      singleParentBonus: 4149,         // Avantage parent isolé (1ère demi-part)
    },

    // === DÉCOTE ===
    decote: {
      singleThreshold: 1929,   // Seuil décote célibataire
      coupleThreshold: 3191,   // Seuil décote couple
      singleCoef: 873,         // Coefficient décote célibataire
      coupleCoef: 1444,        // Coefficient décote couple
    },

    // === CONTRIBUTIONS SOCIALES ===
    socialContributions: {
      csg: 0.092,              // CSG sur revenus du patrimoine
      crds: 0.005,             // CRDS
      prelevementSolidarite: 0.072, // Prélèvement de solidarité
      total: 0.172,            // Total prélèvements sociaux (17.2%)
    },

    // === IMPÔT SUR LES SOCIÉTÉS ===
    corporateTax: {
      reducedRate: 0.15,       // Taux réduit PME
      reducedRateLimit: 42500, // Plafond bénéfice taux réduit
      normalRate: 0.25,        // Taux normal
    },

    // === SEUILS MICRO-ENTREPRISE ===
    microThresholds: {
      bicVente: 188700,        // Seuil CA micro-BIC vente
      bicService: 77700,       // Seuil CA micro-BIC service
      bnc: 77700,              // Seuil recettes micro-BNC
      lmnp: 77700,             // Seuil CA LMNP micro
      foncierMicro: 15000,     // Seuil revenus micro-foncier
    },
  },

  2023: {
    // === ABATTEMENTS SUR REVENUS ===
    salaryDeduction: {
      rate: 0.10,
      min: 472,
      max: 13522,
    },
    pensionDeduction: {
      rate: 0.10,
      min: 422,
      max: 4123,
    },

    // === ABATTEMENTS MICRO-ENTREPRISES ===
    microBIC: {
      vente: 0.71,
      service: 0.50,
    },
    microBNC: 0.34,
    microFoncier: 0.30,
    lmnpMicro: 0.50,

    // === BARÈME IR (Revenus 2023, déclarés en 2024) ===
    irBrackets: [
      { min: 0,      max: 11294,  rate: 0.00 },
      { min: 11294,  max: 28797,  rate: 0.11 },
      { min: 28797,  max: 82341,  rate: 0.30 },
      { min: 82341,  max: 177106, rate: 0.41 },
      { min: 177106, max: Infinity, rate: 0.45 },
    ],

    // === QUOTIENT FAMILIAL ===
    familyQuotient: {
      maxReductionPerHalfPart: 1678,
      singleParentBonus: 3959,
    },

    // === DÉCOTE ===
    decote: {
      singleThreshold: 1840,
      coupleThreshold: 3045,
      singleCoef: 833,
      coupleCoef: 1378,
    },

    // === CONTRIBUTIONS SOCIALES ===
    socialContributions: {
      csg: 0.092,
      crds: 0.005,
      prelevementSolidarite: 0.072,
      total: 0.172,
    },

    // === IMPÔT SUR LES SOCIÉTÉS ===
    corporateTax: {
      reducedRate: 0.15,
      reducedRateLimit: 42500,
      normalRate: 0.25,
    },

    // === SEUILS MICRO-ENTREPRISE ===
    microThresholds: {
      bicVente: 188700,
      bicService: 77700,
      bnc: 77700,
      lmnp: 77700,
      foncierMicro: 15000,
    },
  },

  2022: {
    // === ABATTEMENTS SUR REVENUS ===
    salaryDeduction: {
      rate: 0.10,
      min: 448,
      max: 12829,
    },
    pensionDeduction: {
      rate: 0.10,
      min: 400,
      max: 3912,
    },

    // === ABATTEMENTS MICRO-ENTREPRISES ===
    microBIC: {
      vente: 0.71,
      service: 0.50,
    },
    microBNC: 0.34,
    microFoncier: 0.30,
    lmnpMicro: 0.50,

    // === BARÈME IR (Revenus 2022, déclarés en 2023) ===
    irBrackets: [
      { min: 0,      max: 10777,  rate: 0.00 },
      { min: 10777,  max: 27478,  rate: 0.11 },
      { min: 27478,  max: 78570,  rate: 0.30 },
      { min: 78570,  max: 168994, rate: 0.41 },
      { min: 168994, max: Infinity, rate: 0.45 },
    ],

    // === QUOTIENT FAMILIAL ===
    familyQuotient: {
      maxReductionPerHalfPart: 1592,
      singleParentBonus: 3756,
    },

    // === DÉCOTE ===
    decote: {
      singleThreshold: 1746,
      coupleThreshold: 2888,
      singleCoef: 790,
      coupleCoef: 1307,
    },

    // === CONTRIBUTIONS SOCIALES ===
    socialContributions: {
      csg: 0.092,
      crds: 0.005,
      prelevementSolidarite: 0.072,
      total: 0.172,
    },

    // === IMPÔT SUR LES SOCIÉTÉS ===
    corporateTax: {
      reducedRate: 0.15,
      reducedRateLimit: 38120,
      normalRate: 0.25,
    },

    // === SEUILS MICRO-ENTREPRISE ===
    microThresholds: {
      bicVente: 176200,
      bicService: 72600,
      bnc: 72600,
      lmnp: 72600,
      foncierMicro: 15000,
    },
  },

  2021: {
    // === ABATTEMENTS SUR REVENUS ===
    salaryDeduction: {
      rate: 0.10,
      min: 442,
      max: 12652,
    },
    pensionDeduction: {
      rate: 0.10,
      min: 394,
      max: 3858,
    },

    // === ABATTEMENTS MICRO-ENTREPRISES ===
    microBIC: {
      vente: 0.71,
      service: 0.50,
    },
    microBNC: 0.34,
    microFoncier: 0.30,
    lmnpMicro: 0.50,

    // === BARÈME IR (Revenus 2021, déclarés en 2022) ===
    irBrackets: [
      { min: 0,      max: 10225,  rate: 0.00 },
      { min: 10225,  max: 26070,  rate: 0.11 },
      { min: 26070,  max: 74545,  rate: 0.30 },
      { min: 74545,  max: 160336, rate: 0.41 },
      { min: 160336, max: Infinity, rate: 0.45 },
    ],

    // === QUOTIENT FAMILIAL ===
    familyQuotient: {
      maxReductionPerHalfPart: 1570,
      singleParentBonus: 3704,
    },

    // === DÉCOTE ===
    decote: {
      singleThreshold: 1722,
      coupleThreshold: 2849,
      singleCoef: 779,
      coupleCoef: 1289,
    },

    // === CONTRIBUTIONS SOCIALES ===
    socialContributions: {
      csg: 0.092,
      crds: 0.005,
      prelevementSolidarite: 0.072,
      total: 0.172,
    },

    // === IMPÔT SUR LES SOCIÉTÉS ===
    corporateTax: {
      reducedRate: 0.15,
      reducedRateLimit: 38120,
      normalRate: 0.2667, // Taux normal 26.5% en 2021
    },

    // === SEUILS MICRO-ENTREPRISE ===
    microThresholds: {
      bicVente: 176200,
      bicService: 72600,
      bnc: 72600,
      lmnp: 72600,
      foncierMicro: 15000,
    },
  },
};

/**
 * Récupère la configuration fiscale pour une année donnée
 * @param {number} year - L'année fiscale
 * @returns {Object} La configuration fiscale
 */
export function getFiscalConfig(year) {
  const config = FISCAL_CONFIG[year];
  if (!config) {
    console.warn(`Configuration fiscale non disponible pour ${year}, utilisation de 2024`);
    return FISCAL_CONFIG[2024];
  }
  return config;
}

/**
 * Liste des années disponibles
 */
export const AVAILABLE_FISCAL_YEARS = Object.keys(FISCAL_CONFIG)
  .map(Number)
  .sort((a, b) => b - a);

/**
 * Année fiscale par défaut (la plus récente)
 */
export const DEFAULT_FISCAL_YEAR = AVAILABLE_FISCAL_YEARS[0];
